import {GET} from '../../../js/modules/fetch.ts';
import {errorName} from '../../../js/modules/errors.ts';

type UserGroupSearchResponse = {
  data: Array<{
    id: number;
    name: string;
    description?: string;
  }>;
};

type UserGroupSearchResult = {
  id: number;
  name: string;
  description: string;
};

type CancellableDebounce = ((query: string) => void) & {cancel: () => void};

function debounce(fn: (query: string) => void | Promise<void>, wait: number): CancellableDebounce {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const debounced = (query: string) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = undefined;
      void fn(query);
    }, wait);
  };
  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
    timeout = undefined;
  };
  return debounced;
}

function buildResultElement(group: UserGroupSearchResult): HTMLElement {
  const item = document.createElement('div');
  item.className = 'result';

  const content = document.createElement('div');
  content.className = 'content';

  const title = document.createElement('div');
  title.className = 'title';
  title.textContent = group.name;
  content.append(title);

  if (group.description) {
    const description = document.createElement('div');
    description.className = 'description';
    description.textContent = group.description;
    content.append(description);
  }

  item.append(content);
  return item;
}

export function initCompSearchUserGroupBox() {
  for (const container of document.querySelectorAll<HTMLElement>('[data-user-group-selector]')) {
    const searchURL = container.getAttribute('data-search-url');
    const input = container.querySelector<HTMLInputElement>('input.prompt');
    if (!searchURL || !input) continue;

    const hidden = container.querySelector<HTMLInputElement>('input[type="hidden"]');
    const resultsEl = document.createElement('div');
    resultsEl.className = 'results';
    container.append(resultsEl);

    const itemResults = new Map<HTMLElement, UserGroupSearchResult>();
    let fetchController: AbortController | null = null;

    const hide = () => {
      fetchController?.abort();
      resultsEl.style.display = 'none';
      resultsEl.replaceChildren();
      itemResults.clear();
    };

    const render = (groups: UserGroupSearchResult[]) => {
      if (!groups.length) {
        hide();
        return;
      }

      itemResults.clear();
      resultsEl.replaceChildren(...groups.map((group) => {
        const item = buildResultElement(group);
        itemResults.set(item, group);
        return item;
      }));
      resultsEl.style.display = 'block';
    };

    const select = (item: HTMLElement) => {
      const group = itemResults.get(item);
      if (!group) return;

      input.value = group.name;
      if (hidden) hidden.value = String(group.id);
      input.dispatchEvent(new Event('change', {bubbles: true}));
      hide();
    };

    const search = debounce(async (query: string) => {
      fetchController?.abort();
      if (query.trim().length < 2) {
        hide();
        return;
      }

      const controller = fetchController = new AbortController();
      try {
        const response = await GET(`${searchURL}?q=${encodeURIComponent(query)}`, {signal: controller.signal});
        if (!response.ok) {
          hide();
          return;
        }

        const payload = await response.json() as UserGroupSearchResponse;
        if (!controller.signal.aborted && input.value === query) {
          render(payload.data.map((group) => ({
            id: group.id,
            name: group.name,
            description: group.description ?? '',
          })));
        }
      } catch (err) {
        if (errorName(err) !== 'AbortError') hide();
      }
    }, 200);

    const dismiss = () => {
      search.cancel();
      hide();
    };

    input.addEventListener('input', () => search(input.value));
    input.addEventListener('focus', () => {
      if (itemResults.size) resultsEl.style.display = 'block';
    });
    input.addEventListener('blur', () => {
      search.cancel();
      setTimeout(hide, 150); // hide deferred so a result mousedown can land first
    });
    input.addEventListener('keydown', (event) => {
      const resultEls = Array.from(resultsEl.querySelectorAll<HTMLElement>('.result'));
      if (!resultEls.length) return;

      const index = resultEls.findIndex((item) => item.classList.contains('active'));
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        resultEls[index]?.classList.remove('active');
        const next = event.key === 'ArrowDown' ? (index + 1) % resultEls.length : index <= 0 ? resultEls.length - 1 : index - 1;
        resultEls[next].classList.add('active');
      } else if (event.key === 'Enter' && index >= 0) {
        event.preventDefault();
        select(resultEls[index]);
      } else if (event.key === 'Escape') {
        dismiss();
      }
    });
    resultsEl.addEventListener('mousedown', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.result');
      if (!target) return;
      event.preventDefault();
      select(target);
    });
    document.addEventListener('click', (event) => {
      if (!container.contains(event.target as Node)) dismiss();
    });
  }
}
